import type { ComparisonMetric } from './alignStreams';
import { sliceStreamByTime, downsampleSeries } from './alignStreams';
import type { StreamPoint } from '../workoutStream';
import { lapIndexToSegmentRange, lapDisplayName, type LapRow } from './lapSegments';

export interface MultiLapSelection {
  source: 'A' | 'B';
  index: number;
}

export interface LapOverlaySeries {
  id: string;
  label: string;
  color: string;
  points: { x: number; y: number }[];
}

export const LAP_COLORS_A = ['#4A90D9', '#3B82C4', '#2D74AF', '#1F669A', '#1E5A85', '#174A6E'];
export const LAP_COLORS_B = ['#7C3AED', '#6D28D9', '#5B21B6', '#9333EA', '#A855F7', '#C084FC'];

const METRIC_GETTERS: Record<ComparisonMetric, (p: StreamPoint) => number | null> = {
  power: p => p.power,
  heart_rate: p => p.heartRate,
  cadence: p => p.cadence,
  speed: p => p.speed,
  altitude: p => p.altitude,
};

/** Build time-normalized metric series for one lap (x = seconds from lap start). */
export function buildLapOverlaySeries(
  stream: StreamPoint[],
  laps: LapRow[],
  lapIndex: number,
  metric: ComparisonMetric,
  workoutDurationSec: number,
): { x: number; y: number }[] {
  const seg = lapIndexToSegmentRange(laps, lapIndex, workoutDurationSec);
  if (!seg || !stream.length) return [];

  const slice = sliceStreamByTime(stream, seg.startSec, seg.endSec);
  const getter = METRIC_GETTERS[metric];
  const offset = seg.startSec;

  const points = slice
    .map(p => {
      const y = getter(p);
      if (y == null || !isFinite(y)) return null;
      return { x: p.timeSeconds - offset, y };
    })
    .filter((p): p is { x: number; y: number } => p != null);

  return downsampleSeries(points, 800);
}

export function buildMultiLapOverlaySeries(
  selections: MultiLapSelection[],
  streamA: StreamPoint[],
  streamB: StreamPoint[],
  lapsA: LapRow[],
  lapsB: LapRow[],
  durA: number,
  durB: number,
  metric: ComparisonMetric,
): LapOverlaySeries[] {
  let colorIdxA = 0;
  let colorIdxB = 0;

  return selections
    .map(sel => {
      const stream = sel.source === 'A' ? streamA : streamB;
      const laps = sel.source === 'A' ? lapsA : lapsB;
      const dur = sel.source === 'A' ? durA : durB;
      const lap = laps[sel.index];
      if (!lap) return null;

      const points = buildLapOverlaySeries(stream, laps, sel.index, metric, dur);
      if (points.length < 2) return null;

      const color =
        sel.source === 'A'
          ? LAP_COLORS_A[colorIdxA++ % LAP_COLORS_A.length]
          : LAP_COLORS_B[colorIdxB++ % LAP_COLORS_B.length];

      const prefix = sel.source === 'A' ? 'A' : 'B';
      return {
        id: `${sel.source}-${sel.index}`,
        label: `${prefix} · ${lapDisplayName(lap, sel.index)}`,
        color,
        points,
      };
    })
    .filter((s): s is LapOverlaySeries => s != null);
}

export function multiLapSelectionKey(sel: MultiLapSelection): string {
  return `${sel.source}:${sel.index}`;
}

export function toggleMultiLapSelection(
  prev: MultiLapSelection[],
  source: 'A' | 'B',
  index: number,
  max = 8,
): MultiLapSelection[] {
  const exists = prev.some(s => s.source === source && s.index === index);
  if (exists) return prev.filter(s => !(s.source === source && s.index === index));
  if (prev.length >= max) return prev;
  return [...prev, { source, index }];
}
